/**
 * Parses Godot's `project.godot`, the configuration at a project's `res://` root, into settings
 * keyed by full name. A foreign-format parser outside the resource-slice registry (ADR-0031): no
 * scene names it, so it claims no type name and no bus slot.
 */
import { STRING_ARRAY_FORMS, packedArrayBody } from '../godot/variant/variantParser.js';
import { STRING_LITERAL_RE, STRING_LITERAL_SOURCE, dropTrailingComma, splitTopLevel, } from '../godot/string/string.js';
import { boolSlotValue } from '../godot/variant/variantBool.js';
import { dataDirectoryPath, extensionListPath } from '../godot/project/project.js';
import { isLocaleRightToLeft } from '../godot/text/textServer.js';
import { DEFAULT_CLEAR_COLOR } from '../godot/rendering/rendering.js';
import { DEFAULT_PHYSICS_CLOCK } from '../godot/scene/physicsTicks.js';
import { unquoteLiteral } from './utils.js';
import { colorOr } from '../utils/colorParser.js';
import { perObject } from '../utils/perObject.js';
import { enumOr, parseOptionalFloat, parseOptionalInt } from './valueParsers.js';
import { DEFAULT_PRIMITIVE_MESH_TEXEL_SIZE, primitiveMeshTexelSize } from '../godot/scene/primitiveMesh.js';
import { VOLUMETRIC_FOG_DEFAULTS, } from '../godot/rendering/environment/volumetricFog.js';
import { MSAA_DISABLED } from '../godot/rendering/msaa.js';
import { SSAO_PROJECT_DEFAULTS, SSAO_QUALITIES, } from '../godot/rendering/environment/screenSpaceAmbientOcclusion.js';
import { GlowUpscaleMode } from '../godot/rendering/environment/glow.js';
import { LIGHTMAP_BICUBIC_FILTER_DEFAULT } from '../godot/scene/lightmapGi.js';
import { DEFAULT_ANISOTROPIC_FILTERING_LEVEL, VIEWPORT_DEFAULT_CANVAS_ITEM_TEXTURE_FILTER, VIEWPORT_DEFAULT_CANVAS_ITEM_TEXTURE_REPEAT, VIEWPORT_TEXTURE_FILTERS, VIEWPORT_TEXTURE_REPEATS, viewportCanvasItemTextureDefaults, } from '../godot/scene/viewport.js';
/**
 * A `key=value` line as `parse_tag_assign_eof` reads it (`variant_parser.cpp:1932-1950`): blanks may precede the `=`,
 * and a key is quoted where the writer quotes it, as for a Unicode autoload name (`property_name_encode`,
 * `ustring.cpp:5056-5067`). A feature-tagged override like `renderer/rendering_method.mobile` is a real key.
 */
const KEY_VALUE = new RegExp(String.raw `^(${STRING_LITERAL_SOURCE}|[^\s="]+)\s*=(.*)$`);
/** A `[section]` heading, and any `;` comment after it. Godot's section names are bare identifiers. */
const SECTION = /^\[([A-Za-z_][A-Za-z0-9_]*)\]\s*(?:;.*)?$/;
/**
 * A `project.godot`'s settings, or null when the text is not one. Null, not a throw: absence and
 * malformedness are both ordinary, and the caller uses Godot's defaults either way. Not
 * `TscnParserCore`: bare `[section]` headings and plain `key=value` lines, the INI shape of
 * `importParser.ts`, leave nothing to diagnose.
 */
export function parseProjectSettings(content) {
    const settings = {};
    let section = null;
    let sawSetting = false;
    for (const rawLine of content.split('\n')) {
        const line = rawLine.trim();
        if (line === '' || line.startsWith(';'))
            continue;
        const heading = SECTION.exec(line);
        if (heading) {
            section = heading[1];
            continue;
        }
        // A multi-line quoted value, such as `config/description`, keeps its first line: the
        // continuation has no `=`. No setting this previewer reads is multi-line.
        const pair = KEY_VALUE.exec(line);
        if (!pair)
            continue;
        const [, rawKey, rawValue] = pair;
        const key = unquoteLiteral(rawKey);
        // Godot splits a name across heading and key: `theme/default_theme_scale` under
        // `[gui]` is `gui/theme/default_theme_scale`, as `ProjectSettings.get_setting()`
        // takes it. `config_version` above the first heading keeps its bare name.
        settings[section ? `${section}/${key}` : key] = unquoteLiteral(rawValue.trim());
        sawSetting = true;
    }
    return sawSetting ? settings : null;
}
/** Godot's default when the project does not set the scale. */
export const DEFAULT_THEME_SCALE = 1.0;
/**
 * `gui/theme/default_theme_scale`, the multiplier on every default-theme metric
 * (`ThemeDB::initialize_theme()` in `scene/theme/theme_db.cpp`, default 1.0). Clamped
 * here to 0.5-8 as `make_default_theme` does (`scene/theme/default_theme.cpp:1369`).
 * Each metric rounds after multiplying ({@link scaledGodotTheme}).
 */
export function projectThemeScale(settings) {
    const raw = settings?.['gui/theme/default_theme_scale'];
    // An empty value says nothing, not zero: `Number('')` is 0, which would clamp to 0.5
    // and shrink the UI.
    if (raw === undefined || raw.trim() === '')
        return DEFAULT_THEME_SCALE;
    const scale = Number(raw);
    // An unparseable or non-finite value falls back, not a collapsed UI.
    if (!Number.isFinite(scale))
        return DEFAULT_THEME_SCALE;
    return Math.min(Math.max(scale, 0.5), 8);
}
/**
 * Godot's defaults for `display/window/size/viewport_width` and `_height`
 * (`main/main.cpp`'s `GLOBAL_DEF_BASIC` pair), which frame a scene with no
 * `project.godot`, as Godot does.
 */
export const DEFAULT_VIEWPORT_WIDTH = 1152;
export const DEFAULT_VIEWPORT_HEIGHT = 648;
/**
 * `display/window/size/viewport_*`: the rect a 2D scene is composed against and a root
 * Control anchors to. Per axis, as Godot falls back per setting. Both settings are INT
 * (`project_settings.cpp:1711-1712`), so a fraction truncates. An unreadable or
 * non-positive value takes the default: a zero-width viewport cannot be laid out.
 */
export function projectViewportSize(settings) {
    const axis = (key, fallback) => {
        const value = parseOptionalInt(settings?.[key]);
        return value === undefined || value <= 0 ? fallback : value;
    };
    return {
        width: axis('display/window/size/viewport_width', DEFAULT_VIEWPORT_WIDTH),
        height: axis('display/window/size/viewport_height', DEFAULT_VIEWPORT_HEIGHT),
    };
}
/**
 * `rendering/environment/defaults/default_clear_color`, sRGB, which every opaque viewport clears to
 * (`renderer_viewport.cpp:371,751`), or Godot's default for an absent or malformed value.
 */
export function projectClearColor(settings) {
    return colorOr(settings?.['rendering/environment/defaults/default_clear_color'], DEFAULT_CLEAR_COLOR);
}
const CANVAS_TEXTURE_FILTER_SETTING = 'rendering/textures/canvas_textures/default_texture_filter';
const CANVAS_TEXTURE_REPEAT_SETTING = 'rendering/textures/canvas_textures/default_texture_repeat';
/** The viewport's own defaults, Linear and Disabled, for a project with no settings file. */
const VIEWPORT_CANVAS_TEXTURE_DEFAULTS = viewportCanvasItemTextureDefaults(VIEWPORT_DEFAULT_CANVAS_ITEM_TEXTURE_FILTER, VIEWPORT_DEFAULT_CANVAS_ITEM_TEXTURE_REPEAT);
/**
 * Read once for each settings object: every CanvasItem asks, and a value the setter refuses warns
 * once, not once for each item.
 */
const canvasTextureDefaultsOf = perObject((settings) => viewportCanvasItemTextureDefaults(enumOr(settings[CANVAS_TEXTURE_FILTER_SETTING], VIEWPORT_DEFAULT_CANVAS_ITEM_TEXTURE_FILTER, VIEWPORT_TEXTURE_FILTERS, CANVAS_TEXTURE_FILTER_SETTING), enumOr(settings[CANVAS_TEXTURE_REPEAT_SETTING], VIEWPORT_DEFAULT_CANVAS_ITEM_TEXTURE_REPEAT, VIEWPORT_TEXTURE_REPEATS, CANVAS_TEXTURE_REPEAT_SETTING)));
/**
 * `rendering/textures/canvas_textures/default_texture_filter` and `_repeat`, which the editor's scene root
 * takes as its viewport defaults (`editor_node.cpp:451-460`). Every CanvasItem at PARENT_NODE with no
 * ancestor to defer to resolves to them. A value the viewport setter refuses keeps Linear and Disabled.
 */
export function projectCanvasTextureDefaults(settings) {
    return settings ? canvasTextureDefaultsOf(settings) : VIEWPORT_CANVAS_TEXTURE_DEFAULTS;
}
/** `rendering/lightmapping/primitive_meshes/texel_size`, the world size of a texel in a PrimitiveMesh's UV2. */
export function projectPrimitiveMeshTexelSize(settings) {
    const setting = parseOptionalFloat(settings?.['rendering/lightmapping/primitive_meshes/texel_size']);
    return primitiveMeshTexelSize(setting ?? DEFAULT_PRIMITIVE_MESH_TEXEL_SIZE);
}
/**
 * `rendering/lightmapping/lightmap_gi/use_bicubic_filter`, which every lightmap read takes
 * (`renderer_scene_render_rd.cpp:1732`), or Godot's default for an absent or malformed value.
 */
export function projectUsesLightmapBicubicFilter(settings) {
    return (boolSlotValue(settings?.['rendering/lightmapping/lightmap_gi/use_bicubic_filter']) ??
        LIGHTMAP_BICUBIC_FILTER_DEFAULT);
}
/**
 * `rendering/environment/glow/upscale_mode`, which every glow read takes
 * (`renderer_scene_render_rd.cpp:1717`): bicubic for any mode above Linear, and for an absent or
 * malformed value, as Godot's default is.
 */
export function projectGlowUsesBicubicUpscale(settings) {
    const mode = parseOptionalInt(settings?.['rendering/environment/glow/upscale_mode']);
    return (mode ?? GlowUpscaleMode.BICUBIC) > GlowUpscaleMode.LINEAR;
}
/**
 * The `rendering/environment/volumetric_fog/*` settings and `fog/use_legacy_blending`, which Godot
 * reads once for every Environment (`renderer_scene_render_rd.cpp:1726-1728`). A volume side that
 * is not a positive integer keeps its default, since no volume has an empty side.
 */
export function projectVolumetricFogSettings(settings) {
    const side = (key, fallback) => {
        const value = parseOptionalInt(settings?.[key]);
        return value !== undefined && value > 0 ? value : fallback;
    };
    const flag = (key, fallback) => boolSlotValue(settings?.[key]) ?? fallback;
    return {
        volumeSize: side('rendering/environment/volumetric_fog/volume_size', VOLUMETRIC_FOG_DEFAULTS.volumeSize),
        volumeDepth: side('rendering/environment/volumetric_fog/volume_depth', VOLUMETRIC_FOG_DEFAULTS.volumeDepth),
        useFilter: flag('rendering/environment/volumetric_fog/use_filter', VOLUMETRIC_FOG_DEFAULTS.useFilter),
        useLegacyBlending: flag('rendering/environment/fog/use_legacy_blending', VOLUMETRIC_FOG_DEFAULTS.useLegacyBlending),
    };
}
/**
 * The `rendering/environment/ssao/*` settings, and the `ssil/quality` and `ssil/half_size` that
 * shape the depth buffer SSAO reads (`ss_effects.cpp:151-180`, `:520-547`).
 */
export function projectAmbientOcclusionSettings(settings) {
    const defaults = SSAO_PROJECT_DEFAULTS;
    const quality = (key, fallback) => enumOr(settings?.[key], fallback, SSAO_QUALITIES, key);
    const flag = (key, fallback) => boolSlotValue(settings?.[key]) ?? fallback;
    const real = (key, fallback) => parseOptionalFloat(settings?.[key]) ?? fallback;
    return {
        quality: quality('rendering/environment/ssao/quality', defaults.quality),
        isHalfSize: flag('rendering/environment/ssao/half_size', defaults.isHalfSize),
        adaptiveTarget: real('rendering/environment/ssao/adaptive_target', defaults.adaptiveTarget),
        blurPasses: parseOptionalInt(settings?.['rendering/environment/ssao/blur_passes']) ?? defaults.blurPasses,
        fadeoutFrom: real('rendering/environment/ssao/fadeout_from', defaults.fadeoutFrom),
        fadeoutTo: real('rendering/environment/ssao/fadeout_to', defaults.fadeoutTo),
        ssilQuality: quality('rendering/environment/ssil/quality', defaults.ssilQuality),
        isSsilHalfSize: flag('rendering/environment/ssil/half_size', defaults.isSsilHalfSize),
    };
}
/**
 * `rendering/anti_aliasing/quality/msaa_2d` and `msaa_3d`, which the root viewport takes
 * (`scene/main/scene_tree.cpp:2079-2083`) and the editor's 3D view too
 * (`node_3d_editor_plugin.cpp:3166-3167`). Both default to Disabled.
 */
export function projectMsaaModes(settings) {
    const mode = (key) => parseOptionalInt(settings?.[key]) ?? MSAA_DISABLED;
    return {
        msaa2D: mode('rendering/anti_aliasing/quality/msaa_2d'),
        msaa3D: mode('rendering/anti_aliasing/quality/msaa_3d'),
    };
}
/** The `res://` path a project setting names, or null for no setting and an empty one. */
function projectFilePath(settings, key) {
    return settings?.[key]?.trim() || null;
}
/** `gui/theme/custom`, the project theme (`ThemeDB::initialize_theme`, `scene/theme/theme_db.cpp:52`). */
export function projectThemePath(settings) {
    return projectFilePath(settings, 'gui/theme/custom');
}
/** `gui/theme/custom_font`, the project default font (`theme_db.cpp:53`). */
export function projectFontPath(settings) {
    return projectFilePath(settings, 'gui/theme/custom_font');
}
/**
 * `gui/theme/default_font_generate_mipmaps`, which the default font takes (`theme_db.cpp:64`). Off
 * by default (`text_server.cpp:2387`), so its glyph pages carry no mip chain.
 */
export function projectDefaultFontGeneratesMipmaps(settings) {
    return boolSlotValue(settings?.['gui/theme/default_font_generate_mipmaps']) ?? false;
}
/** `RS::VIEWPORT_ANISOTROPY_16X`, the last level `Viewport.AnisotropicFiltering` names. */
const MAX_ANISOTROPIC_FILTERING_LEVEL = 4;
/**
 * An authored `anisotropic_filtering_level` that names a `Viewport.AnisotropicFiltering` value, or
 * undefined. Godot shifts `1 << level` unchecked (`material_storage.cpp:2461`), so a level outside
 * the enum has no defined meaning to render.
 */
export function parseAnisotropicFilteringLevel(raw) {
    const level = parseOptionalInt(raw);
    return level !== undefined && level >= 0 && level <= MAX_ANISOTROPIC_FILTERING_LEVEL ? level : undefined;
}
/**
 * `rendering/textures/default_filters/anisotropic_filtering_level`, which every viewport takes at
 * construction (`viewport.cpp:5384`). An anisotropic sampler reads `1 << level` taps at most.
 */
export function projectAnisotropicFilteringLevel(settings) {
    return (parseAnisotropicFilteringLevel(settings?.['rendering/textures/default_filters/anisotropic_filtering_level']) ?? DEFAULT_ANISOTROPIC_FILTERING_LEVEL);
}
/** `RS::DECAL_FILTER_LINEAR_MIPMAPS`, Godot's default `rendering/textures/decals/filter` (`rendering_server.cpp:3804`). */
const DEFAULT_DECAL_FILTER = 3;
/** `RS::DECAL_FILTER_LINEAR_MIPMAPS_ANISOTROPIC`, the last filter `RS::DecalFilter` names. */
const MAX_DECAL_FILTER = 5;
/**
 * `rendering/textures/decals/filter`, which the renderer reads for every decal sampler
 * (`renderer_scene_render_rd.cpp:1730`). It numbers the filters as `BaseMaterial3D.TextureFilter`
 * does from Nearest on. A value outside `RS::DecalFilter` keeps the default, as a switch with no
 * case for it binds no sampler (`render_forward_clustered.cpp:3436-3460`).
 */
export function projectDecalFilter(settings) {
    const filter = parseOptionalInt(settings?.['rendering/textures/decals/filter']);
    return filter !== undefined && filter >= 0 && filter <= MAX_DECAL_FILTER ? filter : DEFAULT_DECAL_FILTER;
}
/** Godot's default `rendering/textures/default_filters/texture_mipmap_bias` (`rendering_server.cpp:3803`). */
const DEFAULT_TEXTURE_MIPMAP_BIAS = 0;
/**
 * `rendering/textures/default_filters/texture_mipmap_bias`, which every viewport takes at
 * construction (`viewport.cpp:5383`). The setter does not clamp to the `-2..2` hint
 * (`viewport.cpp:4904-4911`), so a larger value passes through.
 */
export function projectTextureMipmapBias(settings) {
    return (parseOptionalFloat(settings?.['rendering/textures/default_filters/texture_mipmap_bias']) ??
        DEFAULT_TEXTURE_MIPMAP_BIAS);
}
/**
 * `rendering/anti_aliasing/quality/use_debanding`, which the root viewport takes
 * (`scene/main/scene_tree.cpp:2097-2098`).
 */
export function projectUsesDebanding(settings) {
    return boolSlotValue(settings?.['rendering/anti_aliasing/quality/use_debanding']) === true;
}
/**
 * `rendering/environment/screen_space_reflection/half_size`, true by default
 * (`servers/rendering/rendering_server.cpp:3812`): whether the reflection traces at half size.
 */
export function projectTracesHalfSizeReflections(settings) {
    return boolSlotValue(settings?.['rendering/environment/screen_space_reflection/half_size']) ?? true;
}
/**
 * `rendering/driver/depth_prepass/enable`. Without the prepass Godot runs no screen-space ambient
 * occlusion (`render_forward_clustered.cpp:2081-2086`).
 */
export function projectDrawsDepthPrepass(settings) {
    return boolSlotValue(settings?.['rendering/driver/depth_prepass/enable']) ?? true;
}
/**
 * `rendering/viewport/transparent_background`, which the root viewport takes
 * (`scene/main/scene_tree.cpp:2085-2086`). A transparent root clears to `Color(0, 0, 0, 0)`, not
 * the clear colour (`renderer_viewport.cpp:371`).
 */
export function projectTransparentBackground(settings) {
    return boolSlotValue(settings?.['rendering/viewport/transparent_background']) === true;
}
/**
 * The `internationalization/*` settings `Control::is_layout_rtl()` reads, as the four
 * booleans its branches produce ({@link LayoutDirectionEnv}). Two inputs are plain
 * settings (`core/config/project_settings.cpp:1797-1798`), and the RTL locale table is
 * a fixed seven-code list (`modules/text_server_adv/text_server_adv.cpp:534-541`).
 */
export function projectLayoutDirectionEnv(settings) {
    // `GLOBAL_GET_CACHED(bool, …)` booleanizes whatever the ConfigFile holds, so
    // `1` reads as true exactly like `true` does.
    const forceRtl = boolSlotValue(settings?.['internationalization/rendering/force_right_to_left_layout_direction']) === true;
    // The locale comes from `internationalization/locale/test` when set, else the OS
    // (`core/string/translation_server.cpp:592-599`). The OS locale is in no file, so
    // those arms stay left-to-right.
    const testLocale = settings?.['internationalization/locale/test']?.trim() ?? '';
    const applicationLocaleRtl = testLocale !== '' && isLocaleRightToLeft(testLocale);
    // The host's locale, which no scene file states.
    const systemLocaleRtl = false;
    const rootDirection = parseOptionalInt(settings?.['internationalization/rendering/root_node_layout_direction']);
    const rootRtl = rootDirection === 1
        ? false
        : rootDirection === 2
            ? true
            : rootDirection === 3
                ? systemLocaleRtl
                : applicationLocaleRtl;
    return { forceRtl, rootRtl, applicationLocaleRtl, systemLocaleRtl };
}
/** The prefix of an `[autoload]` entry's full setting name. */
const AUTOLOAD_PREFIX = 'autoload/';
/**
 * The name of each `[autoload]` entry. The editor builds one node per entry with a name, and adds each whose script is
 * a tool script to its own tree (`editor/settings/editor_autoload_settings.cpp:859-869`, `:428-455`). Whether a
 * script is a tool script is in the script, so a caller that reads only this file counts every entry.
 */
export function declaredAutoloads(settings) {
    return Object.keys(settings ?? {})
        .filter((key) => key.startsWith(AUTOLOAD_PREFIX))
        .map((key) => key.slice(AUTOLOAD_PREFIX.length))
        .filter((name) => name !== '');
}
/**
 * Each entry of `editor_plugins/enabled`, the `plugin.cfg` paths the editor enables at start (`init_plugins`,
 * `editor/editor_node.cpp:1166-1173`), as written. The conversion to `Vector<String>` stringifies each element
 * (`core/variant/variant.cpp:2082-2091`), so an element that is no string literal counts too. Empty for no array.
 */
export function enabledEditorPlugins(settings) {
    const raw = settings?.['editor_plugins/enabled'];
    const body = raw === undefined ? undefined : packedArrayBody(STRING_ARRAY_FORMS, raw)?.body;
    if (body === undefined)
        return [];
    return dropTrailingComma(splitTopLevel(body)).map((entry) => STRING_LITERAL_RE.test(entry) ? entry.slice(1, -1) : entry);
}
/**
 * Whether the project keeps its data in the hidden directory. A value a BOOL slot cannot read
 * keeps the default, true.
 */
function usesHiddenDataDirectory(settings) {
    return boolSlotValue(settings?.['application/config/use_hidden_project_data_directory']) !== false;
}
/** The `res://` path of the data directory the project names. */
export function projectDataDirectoryPath(settings) {
    return dataDirectoryPath(usesHiddenDataDirectory(settings));
}
/** The `res://` path of the project's GDExtension list, in the data directory the project names. */
export function projectExtensionListPath(settings) {
    return extensionListPath(usesHiddenDataDirectory(settings));
}
/**
 * Written only by {@link physicsClockOf}: one clock for each pair of values, so a project edit that
 * leaves both settings alone keeps the clock, and every live tree keyed on it.
 */
const physicsClocks = new Map([
    [clockKey(DEFAULT_PHYSICS_CLOCK), DEFAULT_PHYSICS_CLOCK],
]);
function clockKey({ ticksPerSecond, jitterFix }) {
    return `${ticksPerSecond}/${jitterFix}`;
}
/** Read once for each settings object. */
const physicsClockOf = perObject((settings) => {
    const rate = parseOptionalInt(settings['physics/common/physics_ticks_per_second']);
    // The setter refuses a rate of 0 or less and keeps the default (`engine.cpp:47-51`).
    const ticksPerSecond = rate !== undefined && rate > 0 ? rate : DEFAULT_PHYSICS_CLOCK.ticksPerSecond;
    // The setter raises a negative fix to 0 (`engine.cpp:75-80`).
    const jitterFix = Math.max(parseOptionalFloat(settings['physics/common/physics_jitter_fix']) ?? DEFAULT_PHYSICS_CLOCK.jitterFix, 0);
    const clock = { ticksPerSecond, jitterFix };
    const known = physicsClocks.get(clockKey(clock));
    if (known)
        return known;
    physicsClocks.set(clockKey(clock), clock);
    return clock;
});
/**
 * `physics/common/physics_ticks_per_second` and `physics_jitter_fix`, which pace the physics ticks
 * and so the transform flushes a scene runs by a frame (`main.cpp:4801-4806`).
 */
export function projectPhysicsClock(settings) {
    return settings ? physicsClockOf(settings) : DEFAULT_PHYSICS_CLOCK;
}
//# sourceMappingURL=projectSettingsParser.js.map